import { DateOnly } from '@if/core/date'
import { EFinancingProgram } from '@if/core/types'
import { FormContainer, useFormContainerContext } from '@if/mui'
import { Grid, Stack } from '@mui/material'
import { Breadcrumb, PageError, PageSpinner } from '@src/components'
import BaseWorksheetForm, {
  BaseWorksheetFormConfig,
  useBaseWorksheetFormConfigFromCreditApp,
} from '@src/components/WorksheetForm'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateEasyPayWorksheet } from '@src/data/api/easypay-worksheet/easypay-worksheet-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { EasyPayWorksheet } from '@src/data/types/EasyPayWorksheetSchema'
import { Merchant } from '@src/data/types/Merchant'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  EasyPayEditSubmitValues,
  EasyPayEditValues,
  getStoredTermAndPlan,
  toEditEasyPayWorksheetDto,
  useEasyPayEditForm,
} from './easyPayEditForm'

const EditEasyPayWorksheetPage = () => {
  const { creditApplication, worksheet, isLoading } = useCreditApplicationData()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')

  if (isLoading || !creditApplication || !merchant || !worksheet) {
    return <PageSpinner isLoading />
  }

  return (
    <PageReady creditApplication={creditApplication} merchant={merchant} worksheet={worksheet as EasyPayWorksheet} />
  )
}

interface PageReadyProps {
  creditApplication: CreditApplication
  merchant: Merchant
  worksheet: EasyPayWorksheet
}

const PageReady = ({ creditApplication, merchant, worksheet }: PageReadyProps) => {
  const navigate = useNavigate()
  const { config, isLoading: isConfigLoading } = useBaseWorksheetFormConfigFromCreditApp({
    creditApplication,
    merchant,
  })

  const { schema, defaultValues, isReady } = useEasyPayEditForm(config, worksheet)

  const [updateWorksheet, isUpdating] = useUpdateEasyPayWorksheet()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()

  const onFormSubmitted = async (values: EasyPayEditSubmitValues) => {
    await updateWorksheet(toEditEasyPayWorksheetDto(values, worksheet))
    await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
    navigate(`/Applications/${EFinancingProgram.EasyPay}/${creditApplication.id}/view`, { replace: true })
  }

  if (isConfigLoading || !isReady || !defaultValues) {
    return <PageSpinner isLoading />
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isUpdating} />
      <FormContainer
        useFormSetup={() => ({ schema, defaultValues })}
        mode="edit"
        onSubmit={(values) => onFormSubmitted(values)}
      >
        <PageContent creditApplication={creditApplication} worksheet={worksheet} config={config} />
      </FormContainer>
    </div>
  )
}

interface PageContentProps {
  creditApplication: CreditApplication
  worksheet: EasyPayWorksheet
  config: BaseWorksheetFormConfig
}

const PageContent = ({ creditApplication, worksheet, config }: PageContentProps) => {
  const { t } = useTranslation()
  const error = useBusinessErrors()
  const { methods } = useFormContainerContext<EasyPayEditValues>()

  const storedTermAndPlan = useMemo(() => getStoredTermAndPlan(worksheet), [worksheet])

  const activationMaxDate = useMemo(() => new DateOnly(config.expiresOn), [config.expiresOn])

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.EasyPay}/${creditApplication.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  return (
    <Stack spacing={2}>
      <PageError errors={error} />
      <Breadcrumb trees={breadCrumbs} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <BaseWorksheetForm config={config}>
            <BaseWorksheetForm.AmountRequested control={methods.control} />
            <BaseWorksheetForm.ActivationDate control={methods.control} maxDate={activationMaxDate} />
            <BaseWorksheetForm.FirstPaymentDate control={methods.control} />
            <BaseWorksheetForm.PaymentFrequency control={methods.control} />
            <BaseWorksheetForm.Term control={methods.control} storedTermAndPlan={storedTermAndPlan} />
          </BaseWorksheetForm>
        </Grid>
      </Grid>
    </Stack>
  )
}

export default EditEasyPayWorksheetPage
